import { XMLParser } from 'fast-xml-parser';
import { MolecularWorkspace, VisibilityFlag } from '@alchemiesolns/molecular-workspace';
import { layoutImplicitHydrogens } from '@alchemiesolns/layout2d';
import { inferLonePairs, inferImplicitHydrogens } from '@alchemiesolns/chemistry-inference';
import { atomicNumberToSymbol } from './elementSymbols';

/** Alchemie workspace target bond length */
const ALCHEMIE_BOND_LENGTH = 6.5;

export type CDXMLParseResult =
  | { ok: true; workspace: MolecularWorkspace }
  | { ok: false; error: { type: 'parse-error' | 'empty-document'; message: string } };

// fast-xml-parser parsed node/bond shapes (attributes become @_-prefixed properties)
interface CdxmlNode {
  '@_id'?: string;
  '@_p'?: string;
  '@_Element'?: string;
  '@_Charge'?: string;
  '@_Radical'?: string;
  '@_NodeType'?: string;
  '@_NumHydrogens'?: string;
  t?: { s: CdxmlTextSpan | CdxmlTextSpan[] };
}

interface CdxmlTextSpan {
  '#text'?: string;
}

interface CdxmlBond {
  '@_B'?: string;
  '@_E'?: string;
  '@_Order'?: string;
  '@_Display'?: string;
}

interface CdxmlFragment {
  n?: CdxmlNode | CdxmlNode[];
  b?: CdxmlBond | CdxmlBond[];
  group?: CdxmlGroup | CdxmlGroup[];
}

interface CdxmlGroup {
  fragment?: CdxmlFragment | CdxmlFragment[];
  group?: CdxmlGroup | CdxmlGroup[];
}

interface CdxmlPage {
  fragment?: CdxmlFragment | CdxmlFragment[];
  group?: CdxmlGroup | CdxmlGroup[];
}

/** Ensure a value is always an array (fast-xml-parser returns single items unwrapped) */
function toArray<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

/** Bond order mapping from CDXML Order attribute to numeric value */
function parseOrder(order: string | undefined): number {
  if (!order) return 1;
  switch (order) {
    case '1': return 1;
    case '2': return 2;
    case '3': return 3;
    case '1.5': return 1.5;
    default: return 1; // dative, ionic, hydrogen, any → treat as single
  }
}

/** Bond display mapping from CDXML Display attribute to bondType */
function parseBondType(display: string | undefined): 'l' | 'd' | 'w' {
  if (!display) return 'l';
  switch (display) {
    case 'WedgeBegin':
    case 'WedgeEnd':
      return 'w';
    case 'WedgedHashBegin':
    case 'WedgedHashEnd':
    case 'Dash':
    case 'Hash':
      return 'd';
    case 'Solid':
    case 'Bold':
    default:
      return 'l';
  }
}

/** Parse "x y" position string into [x, y] */
function parsePosition(p: string | undefined): [number, number] {
  if (!p) return [0, 0];
  const parts = p.trim().split(/\s+/);
  if (parts.length < 2) {
    console.warn(`Malformed position attribute: "${p}", defaulting to (0, 0)`);
    return [0, 0];
  }
  const x = parseFloat(parts[0]);
  const y = parseFloat(parts[1]);
  if (isNaN(x) || isNaN(y)) {
    console.warn(`Non-numeric position values: "${p}", defaulting to (0, 0)`);
    return [0, 0];
  }
  return [x, y];
}

/** Extract text label from node's t > s children */
function extractNicknameLabel(node: CdxmlNode): string {
  if (!node.t) return '?';
  const spans = toArray(
    Array.isArray(node.t) ? (node.t as unknown as CdxmlTextSpan[]) : node.t.s
  );
  let label = '';
  for (const span of spans) {
    label += (typeof span === 'string' ? span : span['#text']) ?? '';
  }
  return label || '?';
}

/** Recursively collect all fragments, descending into group elements */
function collectFragments(container: { fragment?: CdxmlFragment | CdxmlFragment[]; group?: CdxmlGroup | CdxmlGroup[] }): CdxmlFragment[] {
  const fragments: CdxmlFragment[] = [...toArray(container.fragment)];
  for (const group of toArray(container.group)) {
    fragments.push(...collectFragments(group));
  }
  return fragments;
}

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  allowBooleanAttributes: true,
  parseAttributeValue: false,    // keep all attribute values as strings
  trimValues: true,
  isArray: (tagName) => {
    // These tags can appear multiple times; force them to always be arrays
    // to avoid single-element vs array ambiguity
    return ['page', 'fragment', 'n', 'b', 's', 'group'].includes(tagName);
  },
});

/** Pending implicit hydrogen expansion: parent workspace atom ID → count */
interface ImplicitHydrogen {
  parentAtomId: string;
  count: number;
}

export function parseCDXML(input: string): CDXMLParseResult {
  // Step 1: Validate input
  if (!input || !input.trim()) {
    return { ok: false, error: { type: 'parse-error', message: 'Input is empty' } };
  }

  const trimmed = input.trim();
  if (!trimmed.startsWith('<?xml') && !trimmed.startsWith('<CDXML')) {
    return {
      ok: false,
      error: { type: 'parse-error', message: 'Input does not start with <?xml or <CDXML' }
    };
  }

  // Step 2: Parse XML into JS object
  let parsed: Record<string, unknown>;
  try {
    parsed = xmlParser.parse(trimmed);
  } catch (e) {
    return {
      ok: false,
      error: { type: 'parse-error', message: `XML parse error: ${e}` }
    };
  }

  // Step 3: Find CDXML root
  const cdxml = parsed['CDXML'] as Record<string, unknown> | undefined;
  if (!cdxml) {
    return {
      ok: false,
      error: { type: 'empty-document', message: 'No CDXML root element found' }
    };
  }

  // Parse BondLength from document root and compute scale factor to Alchemie units
  const bondLengthAttr = (cdxml as Record<string, string>)['@_BondLength'];
  const bondLength = bondLengthAttr ? parseFloat(bondLengthAttr) : 20.25;
  const scaleFactor = ALCHEMIE_BOND_LENGTH / bondLength;

  // Step 4: Walk page → fragment
  let ws = new MolecularWorkspace();
  const cdxmlIdToWorkspaceId = new Map<number, string>();
  let syntheticIdCounter = -1;
  const implicitHydrogens: ImplicitHydrogen[] = [];
  const atomsWithExplicitNumH = new Set<string>();

  const pages = toArray(cdxml['page'] as CdxmlPage | CdxmlPage[] | undefined);

  for (const page of pages) {
    const fragments = collectFragments(page);

    for (const fragment of fragments) {
      // Step 5: Extract nodes
      const nodes = toArray(fragment.n);
      for (const node of nodes) {
        // Parse node ID
        const idStr = node['@_id'];
        const cdxmlId = idStr ? parseInt(idStr, 10) : syntheticIdCounter--;

        // Parse NodeType
        const nodeType = node['@_NodeType'] ?? 'Element';

        let symbol: string;
        switch (nodeType) {
          case 'Element':
          case '': {
            const elementNum = parseInt(node['@_Element'] ?? '6', 10);
            symbol = atomicNumberToSymbol(elementNum);
            break;
          }
          case 'ExternalConnectionPoint':
            symbol = '*';
            break;
          case 'Nickname':
          case 'Fragment':
            symbol = extractNicknameLabel(node);
            break;
          default:
            console.warn(`Unsupported NodeType "${nodeType}" on node ${cdxmlId}, skipping`);
            continue;
        }

        const [x, rawY] = parsePosition(node['@_p']);
        // CDXML uses y-down (screen coordinates); workspace uses y-up (chemistry convention).
        // Negate y so the existing y-flip in useSVGGeneration produces correct SVG output.
        const y = -rawY;
        const charge = parseInt(node['@_Charge'] ?? '0', 10);
        const radical = node['@_Radical'];
        const radicals = radical === 'Doublet' ? 1 : 0;

        const { workspace: wsWithAtom, atomId: newAtomId } = ws.addAtom({ symbol, x: x * scaleFactor, y: y * scaleFactor, charge, lonePairs: 0, radicals });
        ws = wsWithAtom;
        cdxmlIdToWorkspaceId.set(cdxmlId, newAtomId);

        // Track implicit hydrogens for post-processing
        const hasExplicitNumH = node['@_NumHydrogens'] !== undefined;
        if (hasExplicitNumH) {
          atomsWithExplicitNumH.add(newAtomId);
          const numH = parseInt(node['@_NumHydrogens']!, 10);
          if (numH > 0) {
            implicitHydrogens.push({ parentAtomId: newAtomId, count: numH });
          }
        }
      }

      // Step 6: Extract bonds
      const bonds = toArray(fragment.b);
      for (const bond of bonds) {
        const bAttr = bond['@_B'];
        const eAttr = bond['@_E'];
        if (!bAttr || !eAttr) {
          console.warn('Bond missing B or E attribute, skipping');
          continue;
        }

        const beginId = parseInt(bAttr, 10);
        const endId = parseInt(eAttr, 10);

        const a1Id = cdxmlIdToWorkspaceId.get(beginId);
        const a2Id = cdxmlIdToWorkspaceId.get(endId);

        if (!a1Id) {
          console.warn(`Bond references unknown begin node ${beginId}, skipping`);
          continue;
        }
        if (!a2Id) {
          console.warn(`Bond references unknown end node ${endId}, skipping`);
          continue;
        }

        const order = parseOrder(bond['@_Order']);
        const bondType = parseBondType(bond['@_Display']);

        ws = ws.addBond({ order, bondType }, a1Id, a2Id).workspace;
      }
    }
  }

  // Snapshot atom IDs before adding any H atoms
  const atomIdsBefore = new Set(ws.getAtomIds());

  // Step 7: Expand explicit NumHydrogens from CDXML
  const parentAtomIdsWithH: string[] = [];
  if (implicitHydrogens.length > 0) {
    for (const { parentAtomId, count } of implicitHydrogens) {
      const parentAtom = ws.getAtom(parentAtomId);
      if (!parentAtom) continue;

      parentAtomIdsWithH.push(parentAtomId);
      for (let i = 0; i < count; i++) {
        const { workspace: wsWithH, atomId: hAtomId } = ws.addAtom({
          symbol: 'H',
          x: parentAtom.x,
          y: parentAtom.y,
          charge: 0,
          lonePairs: 0,
          radicals: 0,
        });
        ws = wsWithH.addBond({ order: 1, bondType: 'l' }, parentAtomId, hAtomId).workspace;
      }
    }
  }

  // Step 8: Infer implicit hydrogens for atoms WITHOUT explicit NumHydrogens.
  // CDXML is the authority when NumHydrogens is present; for all other atoms,
  // fill unfilled valence with H atoms.
  ws = inferImplicitHydrogens(ws, {
    bondLength: ALCHEMIE_BOND_LENGTH,
    skipAtomIds: atomsWithExplicitNumH,
  });

  // Step 9: Hide all H atoms that we added (both NumHydrogens and inferred).
  // Only H atoms that were explicit <n> elements in CDXML remain visible.
  for (const atomId of ws.getAtomIds()) {
    if (atomIdsBefore.has(atomId)) continue; // existed before — explicit CDXML atom
    const atom = ws.getAtom(atomId);
    if (atom && atom.symbol === 'H') {
      ws = ws.setVisibility(atomId, VisibilityFlag.Hidden);
    }
  }

  // Step 10: Infer lone pairs — once, after all atoms and bonds are finalized
  ws = inferLonePairs(ws);

  // Step 10b: Hide lone pairs by default (CDXML doesn't display them)
  for (const atomId of ws.getAtomIds()) {
    const atom = ws.getAtom(atomId);
    if (atom && atom.lonePairs > 0) {
      const currentVis = ws.getVisibility(atomId);
      if (currentVis === VisibilityFlag.Visible) {
        ws = ws.setVisibility(atomId, VisibilityFlag.HiddenLP);
      }
      // Don't override Hidden (collapsed H atoms) — they stay Hidden
    }
  }

  // Step 11: Position H atoms from NumHydrogens with VSEPR-correct geometry.
  // inferImplicitHydrogens already positions its own H atoms.
  // This step handles only the CDXML NumHydrogens H atoms added in step 7.
  if (parentAtomIdsWithH.length > 0) {
    ws = layoutImplicitHydrogens(ws, parentAtomIdsWithH, { bondLength: ALCHEMIE_BOND_LENGTH });
  }

  // Step 12: Return result
  return { ok: true, workspace: ws };
}
